import { Menu } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import {
  Sheet as SheetComponent,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { BLACKBOOK_TYPES } from '@/constants'
import { useActiveSection } from '@/landing/contexts/ActiveSectionContext'
import { useLanguage } from '@/landing/contexts/LanguageContext'
import type { HeaderProps } from '@/types/components'
import { getIconForType } from '@/utils/iconUtils'
import { BlackBookItem } from './BlackBookItem'
import { LanguageSwitcher } from './LanguageSwitcher'
import { MobileSection } from './MobileSection'
import { UserMenu } from './UserMenu'

export const Header: React.FC<HeaderProps> = ({ currentUser }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { t } = useLanguage()
  const { activeSection, setActiveSection } = useActiveSection()

  const blackBookSections = [
    {
      id: 'how-to-use',
      title: t('navigation.how_to_use'),
      href: '/blackbook#how-to-use',
      description: t('navigation.how_to_use_desc'),
      icon: getIconForType(''),
      active: true,
      comingSoon: false,
      isExternal: false,
    },
    ...Object.entries(BLACKBOOK_TYPES).map(([apiType, info]) => ({
      id: apiType.toLowerCase().replace(/\s+/g, '-'),
      title: info.displayName,
      href: `/blackbook#${apiType.toLowerCase().replace(/\s+/g, '-')}`,
      description: t(
        `navigation.blackbook_${apiType.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')}_desc`,
      ),
      icon: getIconForType(apiType),
      active: info.active,
      comingSoon: !info.active,
      isExternal: false,
    })),
    // External link sections
    {
      id: 'pilepro-connectors',
      title: t('navigation.pilepro_connectors'),
      href: 'https://www.sheet-pile.com/products/pilepro-connectors',
      description: t('navigation.pilepro_connectors_desc'),
      icon: getIconForType(''),
      active: true,
      comingSoon: false,
      isExternal: true,
    },
    {
      id: 'wadit',
      title: t('navigation.wadit'),
      href: 'https://www.sheet-pile.com/products/wadit',
      description: t('navigation.wadit_desc'),
      icon: getIconForType(''),
      active: true,
      comingSoon: false,
      isExternal: true,
    },
  ]

  // TODO: Temporarily commented out during application development
  // This TOOLS_DATA mapping will be reintegrated once navigation structure is finalized
  // const _toolsData = TOOLS_DATA.map((tool) => ({
  //   ...tool,
  //   title: tool.title.includes('navigation.') ? t(tool.title) : tool.title,
  //   description: tool.description.includes('navigation.') ? t(tool.description) : tool.description,
  // }))

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 navigation">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/" className="flex items-center">
              <img
                src="/images/sheet_pile_app_logo.png"
                alt="Sheet Pile LLC"
                className="h-6 w-auto"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center">
            <NavigationMenu>
              <NavigationMenuList className="space-x-2">
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className="text-sm font-medium"
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                    }}
                  >
                    {t('navigation.black_book')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="p-6">
                    <div className="w-[800px]">
                      <h6 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-4">
                        {t('navigation.black_book_subtitle')}
                      </h6>
                      <div className="grid grid-cols-3 gap-6">
                        {/* Column 1: How to Use + Image */}
                        <div className="space-y-4">
                          {blackBookSections
                            .filter((section) => section.id === 'how-to-use')
                            .map((section) => (
                              <BlackBookItem
                                key={section.title}
                                title={section.title}
                                href={section.href}
                                description={section.description}
                                isActive={activeSection === section.id}
                                comingSoon={section.comingSoon}
                                isExternal={section.isExternal}
                                t={t}
                                onClick={(e) => {
                                  // Allow external links to open normally
                                  if (section.isExternal) {
                                    return
                                  }

                                  // Block navigation for inactive sections
                                  if (section.comingSoon) {
                                    e.preventDefault()
                                    return
                                  }

                                  // If we're already on the blackbook page, prevent navigation and just scroll
                                  if (window.location.pathname === '/blackbook') {
                                    e.preventDefault()
                                    setActiveSection(section.id)
                                    window.history.pushState(null, '', `#${section.id}`)
                                    const targetId =
                                      section.id === 'how-to-use'
                                        ? 'how-to-use'
                                        : 'black-book-sections'
                                    const element = document.getElementById(targetId)
                                    if (element) {
                                      const elementPosition =
                                        element.getBoundingClientRect().top + window.pageYOffset
                                      const offsetPosition = elementPosition - 80

                                      window.scrollTo({
                                        top: offsetPosition,
                                        behavior: 'smooth',
                                      })
                                    }
                                  } else {
                                    setActiveSection(section.id)
                                  }
                                }}
                              />
                            ))}
                          {/* Image placeholder */}
                          <div className="mt-4">
                            <img
                              src="/images/bb.png"
                              alt="Black Book Guide"
                              className="w-full h-auto object-contain rounded-lg"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none'
                              }}
                            />
                          </div>
                        </div>

                        {/* Columns 2 & 3: Product sections */}
                        <div className="col-span-2">
                          <div className="grid grid-cols-2 gap-4">
                            {blackBookSections
                              .filter((section) => section.id !== 'how-to-use')
                              .map((section) => (
                                <BlackBookItem
                                  key={section.title}
                                  title={section.title}
                                  href={section.href}
                                  description={section.description}
                                  isActive={activeSection === section.id}
                                  comingSoon={section.comingSoon}
                                  isExternal={section.isExternal}
                                  t={t}
                                  onClick={(e) => {
                                    // Allow external links to open normally
                                    if (section.isExternal) {
                                      return
                                    }

                                    // Block navigation for inactive sections
                                    if (section.comingSoon) {
                                      e.preventDefault()
                                      return
                                    }

                                    if (window.location.pathname === '/blackbook') {
                                      e.preventDefault()
                                      setActiveSection(section.id)
                                      window.history.pushState(null, '', `#${section.id}`)
                                      const targetId =
                                        section.id === 'how-to-use'
                                          ? 'how-to-use'
                                          : 'black-book-sections'
                                      const element = document.getElementById(targetId)
                                      if (element) {
                                        const elementPosition =
                                          element.getBoundingClientRect().top + window.pageYOffset
                                        const offsetPosition = elementPosition - 80

                                        window.scrollTo({
                                          top: offsetPosition,
                                          behavior: 'smooth',
                                        })
                                      }
                                    } else {
                                      setActiveSection(section.id)
                                    }
                                  }}
                                />
                              ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <div className="relative inline-flex items-center">
                    <button
                      type="button"
                      className="text-sm font-medium px-3 py-2 opacity-60 cursor-not-allowed rounded-md flex items-center gap-2"
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                      }}
                      disabled
                    >
                      {t('navigation.tools')}
                      <span className="inline-flex items-center px-2 py-1 text-[10px] font-bold text-white bg-primary rounded-full">
                        {t('navigation.coming_soon') || 'COMING SOON'}
                      </span>
                    </button>
                  </div>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/glossary"
                    className={navigationMenuTriggerStyle()}
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                    }}
                  >
                    {t('navigation.glossary')}
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/how_it_works"
                    className={navigationMenuTriggerStyle()}
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                    }}
                  >
                    {t('navigation.how_it_works')}
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* User Menu and Language Switcher */}
          <div className="hidden lg:flex items-center space-x-4">
            <a href="https://www.sheet-pile.com" target="_blank" rel="noopener noreferrer">
              <img
                src="/images/logos/logo.sheetpilellc.svg"
                alt="Sheet Pile LLC"
                className="h-6 w-auto"
              />
            </a>
            <LanguageSwitcher />
            <UserMenu currentUser={currentUser} />
          </div>

          {/* Mobile Navigation */}
          <div className="lg:hidden flex items-center space-x-2">
            <LanguageSwitcher />
            <UserMenu currentUser={currentUser} />
            <SheetComponent open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="p-2">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>
                    <div className="flex flex-col items-start space-y-4">
                      <img
                        src="/images/sheet_pile_app_logo.png"
                        alt="Sheet Pile LLC"
                        className="h-6 w-auto"
                      />
                      <a
                        href="https://www.sheet-pile.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src="/images/logos/logo.sheetpilellc.svg"
                          alt="Sheet Pile LLC"
                          className="h-6 w-auto"
                        />
                      </a>
                    </div>
                  </SheetTitle>
                  <SheetDescription className="hidden">
                    Navigation menu for Sheet Pile engineering tools and resources
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-6 px-4 pb-20">
                  {/* Black Book Section */}
                  <MobileSection
                    title={t('navigation.black_book')}
                    subtitle={t('navigation.black_book_subtitle')}
                    items={blackBookSections}
                    onItemClick={() => setMobileMenuOpen(false)}
                    showIcons={false}
                    t={t}
                  />

                  {/* Tools Section */}
                  <div className="relative opacity-50">
                    <div className="p-3 rounded-lg bg-accent/50 cursor-not-allowed">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold">{t('navigation.tools')}</h3>
                          <p className="text-sm text-muted-foreground">
                            {t('navigation.tools_subtitle')}
                          </p>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-bold text-white bg-primary rounded-full">
                          {t('navigation.coming_soon') || 'COMING SOON'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Glossary */}
                  <div>
                    <a
                      href="/glossary"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block p-3 rounded-lg hover:bg-accent transition-colors"
                    >
                      <div className="font-semibold text-lg">{t('navigation.glossary')}</div>
                      <div className="text-sm text-muted-foreground">
                        {t('navigation.glossary_subtitle')}
                      </div>
                    </a>
                  </div>

                  {/* How It Works */}
                  <div>
                    <a
                      href="/how_it_works"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block p-3 rounded-lg hover:bg-accent transition-colors"
                    >
                      <div className="font-semibold text-lg">{t('navigation.how_it_works')}</div>
                      <div className="text-sm text-muted-foreground">
                        {t('navigation.how_it_works_subtitle')}
                      </div>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </SheetComponent>
          </div>
        </div>
      </div>
    </header>
  )
}
